import { useEffect, useState } from 'react';
import { useStore } from './state/store';
import { ContextBanner } from './components/ContextBanner';
import { LastRowPanel } from './components/LastRowPanel';
import { TranscriptPanel } from './components/TranscriptPanel';
import { Grid } from './components/Grid';
import { Toolbar } from './components/Toolbar';
import { SessionPicker } from './components/SessionPicker';
import { ReviewQueue } from './components/ReviewQueue';
import { Settings } from './components/Settings';
import { UserGuide } from './components/UserGuide';
import { Toasts } from './components/Toasts';
import { MicrophoneControl } from './components/MicrophoneControl';
import { RecordingIndicator } from './components/RecordingIndicator';
import { Logo } from './components/Logo';
import { LoginPage } from './pages/LoginPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { AdminPage } from './pages/AdminPage';

type Theme = 'light' | 'dark';

function readInitialTheme(): Theme {
  const saved = localStorage.getItem('lcc-theme');
  if (saved === 'light' || saved === 'dark') return saved;
  if (typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

function LogoWithFallback({ height }: { height: number }) {
  // Render the actual PNG (placed at live-costs-capture/public/logo.png).
  // If the file is missing or 404s, fall back to the inline SVG approximation
  // so the header is never empty. The PNG's black ink + blue accent is
  // inverted under dark mode via the CSS filter on .app-logo.
  const [pngFailed, setPngFailed] = useState(false);
  if (pngFailed) return <Logo height={height} />;
  return (
    <img
      className="app-logo"
      src="/logo.png"
      alt="NVH Dictation Tool"
      style={{ height, width: 'auto' }}
      onError={() => setPngFailed(true)}
    />
  );
}

export function App() {
  const {
    authUser,
    authChecked,
    bootstrapAuth,
    current,
    view,
    loadSettings,
    refreshSessions,
    initTranscription,
    signOut,
    setView,
  } = useStore((s) => ({
    authUser: s.authUser,
    authChecked: s.authChecked,
    bootstrapAuth: s.bootstrapAuth,
    current: s.current,
    view: s.view,
    loadSettings: s.loadSettings,
    refreshSessions: s.refreshSessions,
    initTranscription: s.initTranscription,
    signOut: s.signOut,
    setView: s.setView,
  }));

  const [theme, setTheme] = useState<Theme>(() => readInitialTheme());
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('lcc-theme', theme);
  }, [theme]);

  useEffect(() => {
    void bootstrapAuth();
  }, [bootstrapAuth]);

  useEffect(() => {
    if (!authUser || authUser.mustChangePassword) return;
    void loadSettings();
    void refreshSessions();
    const unsub = initTranscription();
    return unsub;
  }, [authUser, loadSettings, refreshSessions, initTranscription]);

  if (!authChecked) {
    return <div className="loading">Loading…</div>;
  }
  if (!authUser) {
    return <LoginPage />;
  }
  if (authUser.mustChangePassword) {
    return <ChangePasswordPage />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <a className="app-logo-link" href="/" aria-label="NVH Dictation Tool — home">
          <LogoWithFallback height={64} />
        </a>
        <button
          className="theme-toggle"
          onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
        >
          {theme === 'dark' ? '☀ Light' : '☾ Dark'}
        </button>
      </header>
      <SessionPicker />
      <main className="main">
        <UserBar user={authUser} onSignOut={() => void signOut()} onAdmin={() => setView('admin')} />
        {view === 'admin' && authUser.role === 'admin' ? (
          <AdminPage />
        ) : !current ? (
          <div className="welcome">
            <h1>Live Costs Capture</h1>
            <p>Create a new session in the sidebar to start capturing time entries.</p>
          </div>
        ) : (
          <>
            <ContextBanner />
            <Toolbar />
            {view === 'capture' && (
              <>
                <LastRowPanel />
                <MicrophoneControl />
                <div className="capture-split">
                  <Grid />
                  <TranscriptPanel />
                </div>
              </>
            )}
            {view === 'review' && <ReviewQueue />}
            {view === 'settings' && <Settings />}
            {view === 'guide' && <UserGuide />}
          </>
        )}
      </main>
      <RecordingIndicator />
      <Toasts />
    </div>
  );
}

function UserBar({
  user,
  onSignOut,
  onAdmin,
}: {
  user: { displayName: string; email: string; role: 'admin' | 'user' };
  onSignOut: () => void;
  onAdmin: () => void;
}) {
  return (
    <div className="user-bar">
      <div className="user-bar-name">
        <strong>{user.displayName}</strong>
        <span className="muted"> · {user.email}</span>
        {user.role === 'admin' && <span className="badge">admin</span>}
      </div>
      <div className="user-bar-actions">
        {user.role === 'admin' && <button onClick={onAdmin}>Admin</button>}
        <button onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}
